import type { KarmaCompatibility, KarmaNumerology } from "@/lib/karma/types";
import { computeCompatibility } from "@/lib/karma/compatibility";
import { computeArchetype } from "@/lib/karma/archetypes";
import { getElement } from "../../../shared/numerology";

export type LifePathCategory = "dogal" | "uyumlu" | "notr" | "mucadele";

export type MatchScores = {
  attraction: number;
  dailyHarmony: number;
  longevity: number;
  overall: number;
};

export type MatchOverview = {
  category: LifePathCategory;
  categoryLabel: string;
  categoryScore: number;
  relationshipNumber: number;
  scores: MatchScores;
  pin: KarmaCompatibility;
  notes: string[];
};

function escapeRegExp(input: string) {
  return input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function personalizeMatchOverview(
  overview: MatchOverview,
  args: { meName: string; otherName: string },
): MatchOverview {
  const meName = args.meName.trim() || "Sen";
  const otherName = args.otherName.trim() || "Diğer kişi";

  const meRe = new RegExp(`\\b${escapeRegExp("Sen")}\\b`, "g");
  const otherRe = new RegExp(escapeRegExp("Diğer kişi"), "g");

  return {
    ...overview,
    notes: overview.notes.map((note) => note.replace(otherRe, otherName).replace(meRe, meName)),
  };
}

const CATEGORY_RANK: Record<LifePathCategory, number> = {
  dogal: 0,
  uyumlu: 1,
  notr: 2,
  mucadele: 3,
};

const CATEGORY_SCORE: Record<LifePathCategory, number> = {
  dogal: 90,
  uyumlu: 75,
  notr: 60,
  mucadele: 45,
};

const CATEGORY_LABEL: Record<LifePathCategory, string> = {
  dogal: "Doğal",
  uyumlu: "Uyumlu",
  notr: "Nötr",
  mucadele: "Mücadele",
};

function reduceToSingleDigit(n: number): number {
  let total = Math.abs(Math.round(n));
  while (total >= 10) {
    total = String(total)
      .split("")
      .map((ch) => ch.charCodeAt(0) - 48)
      .filter((d) => d >= 0 && d <= 9)
      .reduce((acc, d) => acc + d, 0);
  }
  return total || 1;
}

function clampScore(n: number) {
  return Math.max(0, Math.min(100, Math.round(n)));
}

function lpValue(n: KarmaNumerology): number {
  return n.lifePathMaster ?? n.destinyNumber;
}

// Kitap tablosu: A (satır) -> B (sütun) kategori lookup
const LIFE_PATH_TABLE: Record<
  number,
  Record<LifePathCategory, readonly number[]>
> = {
  1: {
    dogal: [1, 3, 5],
    uyumlu: [2, 4, 22, 8],
    mucadele: [6, 33],
    notr: [7, 9],
  },
  2: {
    dogal: [2, 11, 4, 22, 8],
    uyumlu: [6, 33],
    mucadele: [1, 3, 5, 9],
    notr: [7],
  },
  11: {
    dogal: [2, 11, 4, 22, 8],
    uyumlu: [6, 33],
    mucadele: [1, 3, 5, 9],
    notr: [7],
  },
  3: {
    dogal: [1, 3, 5],
    uyumlu: [6, 33, 9],
    mucadele: [2, 11],
    notr: [4, 22, 7, 8],
  },
  4: {
    dogal: [2, 11, 4, 22, 8],
    uyumlu: [1, 6, 33, 7],
    mucadele: [5, 9],
    notr: [3],
  },
  22: {
    dogal: [2, 11, 4, 22, 8],
    uyumlu: [1, 6, 33, 7],
    mucadele: [5, 9],
    notr: [3],
  },
  5: {
    dogal: [1, 3, 5, 7],
    uyumlu: [9],
    mucadele: [4, 22, 6, 33, 8],
    notr: [2, 11],
  },
  6: {
    dogal: [6, 33, 9],
    uyumlu: [2, 11, 3, 4, 22, 8],
    mucadele: [1, 5, 7],
    notr: [],
  },
  33: {
    dogal: [6, 33, 9],
    uyumlu: [2, 11, 3, 4, 22, 8],
    mucadele: [1, 5, 7],
    notr: [],
  },
  7: {
    dogal: [5, 7],
    uyumlu: [4, 22],
    mucadele: [2, 11, 6, 33, 8, 9],
    notr: [1, 3],
  },
  8: {
    dogal: [2, 11, 4, 22, 8],
    uyumlu: [1, 6, 33],
    mucadele: [5, 7, 9],
    notr: [3],
  },
  9: {
    dogal: [9],
    uyumlu: [3, 6, 33, 9],
    mucadele: [1, 2, 11, 5],
    notr: [4, 22, 7, 8],
  },
};

function lookupCategory(a: number, b: number): LifePathCategory {
  const row = LIFE_PATH_TABLE[a] ?? LIFE_PATH_TABLE[reduceToSingleDigit(a)];
  if (!row) return "notr";

  if (row.dogal.includes(b)) return "dogal";
  if (row.uyumlu.includes(b)) return "uyumlu";
  if (row.mucadele.includes(b)) return "mucadele";
  if (row.notr.includes(b)) return "notr";
  return "notr";
}

export function getLifePathCategory(a: KarmaNumerology, b: KarmaNumerology) {
  const av = lpValue(a);
  const bv = lpValue(b);
  const aToB = lookupCategory(av, bv);
  const bToA = lookupCategory(bv, av);

  return CATEGORY_RANK[aToB] >= CATEGORY_RANK[bToA] ? aToB : bToA;
}

function numberSimilarityScore(a: number, b: number) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 50;
  if (a === b) return 90;
  const sum = a + b;
  if (sum === 10) return 85;
  if (a % 2 === b % 2) return 75;
  return 65;
}

const RELATIONSHIP_THEMES: Record<number, string> = {
  1: "başlatma, liderlik, net hedef",
  2: "uyum, diplomasi, ortak karar",
  3: "iletişim, yaratıcılık, görünürlük",
  4: "plan, sistem, güvenli yapı",
  5: "değişim, hareket, deneyim",
  6: "şifa, sorumluluk, destek",
  7: "derinlik, araştırma, anlam",
  8: "sonuç, güç, değer/para",
  9: "tamamlama, bilgelik, paylaşım",
};

const WORK_STYLE: Record<number, string> = {
  1: "hızlı karar + başlangıç",
  2: "koordinasyon + arabuluculuk",
  3: "iletişim + fikir üretimi",
  4: "operasyon + sistem kurma",
  5: "pazarlama + yeni fırsat",
  6: "insan odaklı bakım + kalite",
  7: "analiz + derin çalışma",
  8: "yönetim + hedef/finans",
  9: "topluluk + anlam/amaç",
};

const NUMBER_EXPLANATIONS: Record<number, string> = {
  1: "bağımsızlık, hız ve inisiyatif",
  2: "uyum, hassasiyet ve birlikte karar",
  3: "iletişim, neşe ve ifade",
  4: "düzen, güven ve istikrar",
  5: "özgürlük, değişim ve hareket",
  6: "aidiyet, sorumluluk ve bakım",
  7: "derinlik, sorgulama ve içgörü",
  8: "güç, sonuç ve strateji",
  9: "anlam, şefkat ve tamamlama",
};

const ELEMENT_EXPLANATIONS: Record<string, string> = {
  Hava: "zihinsel tempo, iletişim ve fikir alışverişi",
  Su: "duygusal bağ, sezgi ve yakınlık",
  Ateş: "motivasyon, cesaret ve harekete geçme",
  Toprak: "güven, düzen ve somut adımlar",
  Denge: "farklı alanları bir arada tutma ve merkezde kalma",
};

function profileElementLabel(n: KarmaNumerology) {
  return getElement(n.birthYear) || "Denge";
}

function describeNumberPair(args: {
  label: string;
  a: number;
  b: number;
  aLabel?: string;
  bLabel?: string;
  sameMeans: string;
  complementMeans: string;
  differentMeans: string;
}) {
  const {
    label,
    a,
    b,
    aLabel = "Sen",
    bLabel = "Diğer kişi",
    sameMeans,
    complementMeans,
    differentMeans,
  } = args;

  if (!Number.isFinite(a) || !Number.isFinite(b)) return `${label}: Bu alan için net bir yorum üretilemedi.`;

  const aTheme = NUMBER_EXPLANATIONS[reduceToSingleDigit(a)] ?? "kişisel ritim";
  const bTheme = NUMBER_EXPLANATIONS[reduceToSingleDigit(b)] ?? "kişisel ritim";

  if (a === b) {
    return `${label}: İkinizde de ${a} titreşimi baskın. Bu, ilişkinin bu alanında benzer beklentilerle hareket ettiğinizi gösterir; ana tema ${aTheme}. ${sameMeans} Bu benzerlik güçlü bir rahatlık sağlar, ancak tek bir bakış açısına sıkışmamak için düzenli açık iletişim faydalı olur.`;
  }

  if (a + b === 10) {
    return `${label}: ${aLabel} ${a}, ${bLabel} ${b}. Bu ikili 10 tamamlayıcı kombinasyonudur; biri ${aTheme}, diğeri ${bTheme} tarafını doğal taşır. ${complementMeans} Dengeyi korumak için görev ve beklentilerin baştan net konuşulması ilişkiyi belirgin şekilde rahatlatır.`;
  }

  const dist = Math.abs(a - b);
  if (dist >= 5) {
    return `${label}: ${aLabel} ${a}, ${bLabel} ${b}. Burada ritim farkı yüksek olduğu için ihtiyaçlar farklı hızlarda görünür; biri ${aTheme}, diğeri ${bTheme} tarafına yaslanır. ${differentMeans} Bu fark sorun değil; rol paylaşımı, sınırlar ve iletişim dili net olduğunda ilişki daha güvenli ilerler.`;
  }

  return `${label}: ${aLabel} ${a}, ${bLabel} ${b}. Sayılar farklı olsa da ortak zemin kurulabilir; ${aTheme} ve ${bTheme} birbirini besleyebilir. ${differentMeans} Düzenli geri bildirim ve küçük uzlaşmalar bu alanı hızla güçlendirir.`;
}

function pinInsightNotes(pinA: KarmaCompatibility, a: KarmaNumerology, b: KarmaNumerology) {
  const notes: string[] = [];

  if (pinA.sharedDigits >= 6) notes.push("PIN kalıbı benzer: ortak dil daha hızlı kurulur.");
  else if (pinA.sharedDigits <= 3) notes.push("PIN kalıbı farklı: merak + açık iletişim büyütür.");

  if (pinA.elementBalance >= 80) notes.push("Element dengesi yakın: tempo ve günlük ritim benzer.");
  else if (pinA.elementBalance <= 45) notes.push("Element dengesi farklı: alan tanımak ve iş bölümü uyumu artırır.");

  const countsA = countPinDigits(a.pin);
  const countsB = countPinDigits(b.pin);

  const complementsAB = findComplements(countsA, countsB).slice(0, 1);
  for (const d of complementsAB) {
    notes.push(`Sende ${d} düşük, onda ${d} güçlü → bu enerji sende daha kolay açılabilir.`);
  }

  const complementsBA = findComplements(countsB, countsA).slice(0, 1);
  for (const d of complementsBA) {
    notes.push(`Onda ${d} düşük, sende ${d} güçlü → bu enerji onda daha kolay açılabilir.`);
  }

  const clashes = findClashes(countsA, countsB).slice(0, 1);
  for (const d of clashes) {
    notes.push(`İkinizde de ${d} baskın → aynı konuda ‘benim dediğim’ sürtüşmesi olabilir.`);
  }

  return notes;
}

function archetypeNote(a: KarmaNumerology, b: KarmaNumerology) {
  const aArch = computeArchetype(a);
  const bArch = computeArchetype(b);

  if (aArch.digit === bArch.digit) {
    return `Arketip: İkiniz de ${aArch.name} arketipindesiniz. Bu durum motivasyon kaynaklarınızı benzerleştirir; karar alma biçimi, öncelikler ve ilişki beklentileri daha hızlı hizalanır. Güçlü taraflarınız ${aArch.strengths}; gölgede ise ${aArch.shadows} temaları tetiklenebilir. Bu nedenle aynı anda yükselen tepkilerde kısa mola verip sonra konuşmak ilişkiye çok iyi gelir.`;
  }

  if (aArch.digit + bArch.digit === 10) {
    return `Arketip: Sen ${aArch.name}, Diğer kişi ${bArch.name}. Bu ikili tamamlayıcı bir denge kurar; biri açar, diğeri toplar ve yapılandırır. Doğru rol paylaşımıyla hem duygusal hem pratik alanda hızlı uyum yakalanabilir. Anahtar nokta, kimin hangi konuda liderlik alacağını baştan netleştirmektir.`;
  }

  return `Arketip: Sen ${aArch.name}, Diğer kişi ${bArch.name}. Motivasyon biçimleriniz farklı olduğu için ilişki içinde bazen öncelik çatışması yaşanabilir. Buna rağmen ortak niyet ve iletişim kuralları net olduğunda bu fark, çatışma yerine dengeye dönüşür. Birbirinizin güçlü taraflarını doğru yerde kullandığınızda ilişki daha olgun bir akışa geçer.`;
}

const CATEGORY_INTRO_TEMPLATES: Record<LifePathCategory, readonly string[]> = {
  dogal: [
    "Doğal akış: Sen ve Diğer kişi birlikteyken ilişki dili kendiliğinden yumuşar.",
    "Doğal uyum: İkinizin ritmi benzer; günlük hayatta sürtünme az, akış yüksek.",
    "Doğal birliktelik: Birlikte karar almak ve ilerlemek çoğu zaman kolaylaşır.",
  ],
  uyumlu: [
    "Uyumlu eşleşme: Sen ve Diğer kişi birbirini destekleyen bir denge kurabilir.",
    "Uyumlu akış: Farklı güçlü yanlar doğru paylaşımda birbirini tamamlar.",
    "Uyumlu yapı: İlişki, arkadaşlık ve iş birliğinde ortak zemin kurmak kolaylaşır.",
  ],
  notr: [
    "Nötr denge: Sen ve Diğer kişi doğru iletişimle güçlü bir bağ kurabilir.",
    "Nötr uyum: Akış otomatik değil; ama net beklenti konuşmaları ilişkiyi güçlendirir.",
    "Nötr başlangıç: Benzerlikten çok bilinçli iş birliği ön plana çıkar.",
  ],
  mucadele: [
    "Mücadeleli eşleşme: Sen ve Diğer kişi farklı ritimlerle ilerleyebilir; açık iletişim belirleyici olur.",
    "Mücadele teması: Tetiklenen alanlarda sınır ve nezaket dili uyumu artırır.",
    "Mücadeleli akış: Zorluk potansiyeli var; doğru rol paylaşımıyla ilişki dengelenebilir.",
  ],
};

function pickBySeed<T>(items: readonly T[], seed: number): T {
  return items[Math.abs(seed) % items.length] as T;
}

function openingCompatibilityNote(args: {
  category: LifePathCategory;
  relationshipNumber: number;
  a: KarmaNumerology;
  b: KarmaNumerology;
}) {
  const { category, relationshipNumber, a, b } = args;
  const intro = pickBySeed(
    CATEGORY_INTRO_TEMPLATES[category],
    a.destinyNumber * 17 + b.destinyNumber * 13 + relationshipNumber,
  );
  const theme = RELATIONSHIP_THEMES[relationshipNumber] ?? "ortak tema";
  return `Doğal Akış: ${intro} Bu ilişkinin ortak yolu ${relationshipNumber}; ana tema ${theme}. Bu tema, ilişkinin birlikte nasıl büyüdüğünü ve hangi alanda birbirinizi daha kolay desteklediğinizi gösterir.`;
}

function buildCompatibilityNotes(args: {
  category: LifePathCategory;
  relationshipNumber: number;
  a: KarmaNumerology;
  b: KarmaNumerology;
  pin: KarmaCompatibility;
}) {
  const { category, relationshipNumber, a, b, pin } = args;

  const notes: string[] = [];

  notes.push(openingCompatibilityNote({ category, relationshipNumber, a, b }));

  notes.push(archetypeNote(a, b));

  notes.push(
    describeNumberPair({
      label: "Kalp arzusu",
      a: a.heartDesireNumber,
      b: b.heartDesireNumber,
      sameMeans: "duygusal ihtiyaçlar benzer; kolay anlaşılır.",
      complementMeans: "biri duyguyu açar, diğeri dengeler; doğru dil bulunursa çok besleyici.",
      differentMeans: "duyguyu gösterme biçimi farklı; tetiklenince ‘niyet’ hatırlatmak işe yarar.",
    }),
  );

  const workA = WORK_STYLE[reduceToSingleDigit(a.expressionNumber)] ?? "üretim";
  const workB = WORK_STYLE[reduceToSingleDigit(b.expressionNumber)] ?? "üretim";
  notes.push(
    describeNumberPair({
      label: `İş/üretim dili (${workA} × ${workB})`,
      a: a.expressionNumber,
      b: b.expressionNumber,
      sameMeans: "iş yapma tarzı benzer; süreçler kolay oturur.",
      complementMeans: "görev paylaşımı iyi yapılırsa hızlı sonuç alınır.",
      differentMeans: "biri süreç, biri hız isteyebilir; hedef + yöntem ayrı konuşulsun.",
    }),
  );

  const elA = profileElementLabel(a);
  const elB = profileElementLabel(b);
  const elATheme = ELEMENT_EXPLANATIONS[elA] ?? "kişisel ritim";
  const elBTheme = ELEMENT_EXPLANATIONS[elB] ?? "kişisel ritim";

  if (elA === elB) {
    notes.push(
      `Element Ritmi: İkinizde de ${elA} elementi baskın. Bu, ${elATheme} alanlarında benzer tempo ile ilerlediğinizi gösterir. Günlük hayatta aynı dilde anlaşmayı kolaylaştırır; yine de duygusal ihtiyaçları açıkça konuşmak bağın daha dengeli kalmasını sağlar.`,
    );
  } else {
    notes.push(
      `Element Ritmi: Sende ${elA}, onda ${elB} elementi baskın. Bu fark, ${elATheme} ile ${elBTheme} arasında tamamlayıcı bir denge kurabilir. İlişkinin rahat akması için hız, beklenti ve alan ihtiyacını düzenli konuşmak önemlidir.`,
    );
  }

  const sharedKarmic = Array.from(
    new Set((a.karmicCodes ?? []).filter((c) => (b.karmicCodes ?? []).includes(c))),
  );
  if (sharedKarmic.length) {
    notes.push(
      `Ortak Karmik Ders: ${sharedKarmic.slice(0, 3).join(", ")} kodları ikinizde de görünüyor. Bu, benzer tetik noktalarında benzer şekilde zorlanabileceğinizi gösterir. İlişkide yargı yerine şefkatli dil kurmak, sınırları netleştirmek ve aynı konuda tekrar eden döngüleri fark etmek bu bağı daha sağlıklı hale getirir.`,
    );
  } else {
    notes.push(
      "Ortak Karmik Ders: Karmik kodlarınız birebir aynı değil. Bu farklılık, ilişkiye iki ayrı bakış açısı getirir ve birbirinizi tek yönlü değil çok yönlü geliştirmenize yardımcı olur.",
    );
  }

  notes.push(...pinInsightNotes(pin, a, b));

  const unique = Array.from(new Set(notes.map((n) => n.trim()).filter(Boolean)));
  return unique.slice(0, 7);
}

function countPinDigits(pin: number[]) {
  const counts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 };
  for (const raw of pin) {
    const d = reduceToSingleDigit(raw);
    if (d >= 1 && d <= 9) counts[d] += 1;
  }
  return counts;
}

function findComplements(countsA: Record<number, number>, countsB: Record<number, number>) {
  // A'da eksik (0) ve B'de güçlü (>=2)
  const result: number[] = [];
  for (let d = 1 as const; d <= 9; d += 1) {
    if ((countsA[d] ?? 0) === 0 && (countsB[d] ?? 0) >= 2) result.push(d);
  }
  return result;
}

function findClashes(countsA: Record<number, number>, countsB: Record<number, number>) {
  // İkisi de baskın (>=3)
  const result: number[] = [];
  for (let d = 1 as const; d <= 9; d += 1) {
    if ((countsA[d] ?? 0) >= 3 && (countsB[d] ?? 0) >= 3) result.push(d);
  }
  return result;
}

export function computeMatchOverview(a: KarmaNumerology, b: KarmaNumerology): MatchOverview {
  const category = getLifePathCategory(a, b);
  const categoryScore = CATEGORY_SCORE[category];

  const relationshipNumber = reduceToSingleDigit(a.destinyNumber + b.destinyNumber);

  const lpScore = categoryScore;
  const heartScore = numberSimilarityScore(a.heartDesireNumber, b.heartDesireNumber);
  const expressionScore = numberSimilarityScore(a.expressionNumber, b.expressionNumber);

  const pin = computeCompatibility(a.pin, b.pin);
  const pinScore = pin.score;

  const attraction = clampScore(0.2 * lpScore + 0.45 * heartScore + 0.15 * expressionScore + 0.2 * pinScore);
  const dailyHarmony = clampScore(0.35 * lpScore + 0.15 * heartScore + 0.25 * expressionScore + 0.25 * pinScore);
  const longevity = clampScore(0.45 * lpScore + 0.15 * heartScore + 0.2 * expressionScore + 0.2 * pinScore);

  const overall = clampScore(0.4 * dailyHarmony + 0.3 * longevity + 0.3 * attraction);

  const notes = buildCompatibilityNotes({ category, relationshipNumber, a, b, pin });

  return {
    category,
    categoryLabel: CATEGORY_LABEL[category],
    categoryScore,
    relationshipNumber,
    scores: {
      attraction,
      dailyHarmony,
      longevity,
      overall,
    },
    pin,
    notes,
  };
}
